/**
 * Platform Metrics Utility
 * Provides consistent baseline metrics across the entire NeuroMesh platform
 * 
 * Metrics Derivation:
 * - Total Nodes: Based on virtual nodes count (50-150) + network multiplier
 * - Active Validators: Derived as 1.0-1.5% of total nodes
 * - Total Inference: Calculated from nodes × average inference per node (150-250)
 * - Network TPS: Derived from current throughput with realistic jitter
 * 
 * Daily Chart Seeding:
 * - Uses date-based seed (YYYY-MM-DD) to generate deterministic chart data
 * - Chart stays constant for the entire day, regenerates at midnight
 * - Stored in memory to avoid re-computation on page navigation
 */

// Seed-based random number generator for deterministic charts
function seededRandom(seed) {
  const x = Math.sin(seed++) * 10000;
  return x - Math.floor(x);
}

// Get today's date string for seeding
function getTodayDateString() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

// Generate deterministic 24h chart data with oscillation and upward trend at end
export function generate24hThroughputData() {
  const dateStr = getTodayDateString();
  const cacheKey = `superbrain_24h_${dateStr}`;
  
  // Check if we already generated today's data
  if (typeof window !== 'undefined' && window[cacheKey]) {
    return window[cacheKey];
  }
  
  // Generate deterministic data based on date
  const seed = dateStr.split('-').reduce((acc, val) => acc + parseInt(val), 0);
  const data = [];
  const now = new Date();
  
  // Baseline TPS around 2500-3000
  const baseline = 2700;
  
  for (let i = 23; i >= 0; i--) {
    const hourSeed = seed + i * 100;
    const progress = (23 - i) / 23; // 0 to 1 progress through the day
    
    // Create oscillating wave pattern (3-4 waves)
    const wave1 = Math.sin(progress * Math.PI * 3) * 400; // Primary wave
    const wave2 = Math.sin(progress * Math.PI * 4.5 + 1) * 200; // Secondary wave
    
    // Upward trend at the end (last 6 hours)
    const upwardTrend = progress > 0.75 ? (progress - 0.75) * 4 * 800 : 0;
    
    // Small deterministic jitter
    const jitter = (seededRandom(hourSeed) - 0.5) * 150;
    
    const value = Math.floor(baseline + wave1 + wave2 + upwardTrend + jitter);
    
    data.push({
      time: new Date(now - i * 3600000).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      tps: Math.max(1800, Math.min(5500, value)) // Clamp between 1800-5500
    });
  }
  
  // Cache it
  if (typeof window !== 'undefined') {
    window[cacheKey] = data;
  }
  
  return data;
}

// Get platform baseline metrics (shared across dashboard, superbrain, etc.)
export function getPlatformMetrics(virtualNodesCount = 71) {
  /**
   * Baseline Derivation:
   * - Virtual nodes in platform: 50-150 (actual count from backend)
   * - Network simulation multiplier: 500-700x to simulate global scale
   * - Total simulated nodes: virtualNodes × multiplier
   */
  
  // Base from virtual nodes, multiply for global network simulation
  const networkMultiplier = 640; // Gives us ~45,000 nodes for 71 virtual nodes
  const totalNodes = virtualNodesCount * networkMultiplier;
  
  // Add small daily drift (±0.5%)
  const dateStr = getTodayDateString();
  const dateSeed = dateStr.split('-').reduce((acc, val) => acc + parseInt(val), 0);
  const dailyDrift = (seededRandom(dateSeed + 1000) - 0.5) * 0.01; // ±0.5%
  const driftedNodes = Math.floor(totalNodes * (1 + dailyDrift));
  
  // Small session jitter (±50 nodes) for realism
  const sessionJitter = Math.floor((Math.random() - 0.5) * 100);
  const finalNodes = driftedNodes + sessionJitter;
  
  // Active Validators: 1.0-1.2% of nodes
  const validatorRatio = 0.011 + (seededRandom(dateSeed + 2000) * 0.002); // 1.1% ± 0.1%
  const baseValidators = Math.floor(finalNodes * validatorRatio);
  const validatorJitter = Math.floor((Math.random() - 0.5) * 10); // ±5 validators
  const activeValidators = Math.max(120, Math.min(1200, baseValidators + validatorJitter));
  
  // Total Inference: nodes × average inference per node (150-250)
  const inferencePerNode = 180 + seededRandom(dateSeed + 3000) * 70;
  const totalInference = Math.floor(finalNodes * inferencePerNode);
  
  // Network TPS: stable with tiny jitter
  const baseTPS = 2800 + Math.floor(seededRandom(dateSeed + 4000) * 400); // 2800-3200
  const tpsJitter = Math.floor((Math.random() - 0.5) * 50);
  const networkTPS = baseTPS + tpsJitter;
  
  return {
    totalNodes: finalNodes,
    activeValidators,
    totalInference,
    networkTPS,
    virtualNodesCount
  };
}

// Format large numbers (K, M, B)
export function formatLargeNumber(num) {
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(1) + 'B';
  }
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
}
